import { useEffect, useState, useCallback } from 'react';
import {
  api,
  type SlashCommandFull,
  type SlashCommandCreate,
  type SlashHandlerType,
  type SlashSyncMode,
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/Dialog';
import { Plus, Edit, Trash2, RefreshCw, Shield } from 'lucide-react';

const HANDLER_TYPES: SlashHandlerType[] = ['internal_op', 'skill', 'agent', 'script', 'pipeline'];
const SYNC_MODES: SlashSyncMode[] = ['auto', 'sync', 'async'];

const HANDLER_TYPE_VARIANT: Record<SlashHandlerType, 'brand' | 'info' | 'success' | 'warning' | 'danger' | 'default'> = {
  internal_op: 'info',
  skill: 'success',
  agent: 'brand',
  script: 'warning',
  pipeline: 'default',
};

interface FormState {
  slug: string;
  name: string;
  description: string;
  handler_type: SlashHandlerType;
  handler_ref: string;
  scopes: string;
  sync_mode: SlashSyncMode;
  is_active: boolean;
  is_global: boolean;
  handler_config: string;
  args_schema: string;
}

const EMPTY_FORM: FormState = {
  slug: '',
  name: '',
  description: '',
  handler_type: 'internal_op',
  handler_ref: '',
  scopes: 'telegram,dashboard',
  sync_mode: 'auto',
  is_active: true,
  is_global: false,
  handler_config: '{}',
  args_schema: '{}',
};

export function AdminSlashCommandsPage() {
  const { tenant } = useAuth();
  const isAdmin = !!tenant?.is_admin;

  const [commands, setCommands] = useState<SlashCommandFull[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<SlashCommandFull | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SlashCommandFull | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.listSlashCommandsAdmin();
      setCommands(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro listando commands');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [load, isAdmin]);

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditing(null);
    setCreating(true);
  }

  function openEdit(cmd: SlashCommandFull) {
    setForm({
      slug: cmd.slug,
      name: cmd.name,
      description: cmd.description ?? '',
      handler_type: cmd.handler_type,
      handler_ref: cmd.handler_ref,
      scopes: cmd.scopes.join(','),
      sync_mode: cmd.sync_mode,
      is_active: cmd.is_active,
      is_global: cmd.is_global,
      handler_config: JSON.stringify(cmd.handler_config ?? {}, null, 2),
      args_schema: JSON.stringify(cmd.args_schema ?? {}, null, 2),
    });
    setEditing(cmd);
    setCreating(false);
  }

  function closeForm() {
    setEditing(null);
    setCreating(false);
    setForm(EMPTY_FORM);
  }

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      let handlerConfig: Record<string, unknown> = {};
      let argsSchema: Record<string, unknown> = {};
      try { handlerConfig = JSON.parse(form.handler_config); } catch { throw new Error('handler_config: JSON inválido'); }
      try { argsSchema = JSON.parse(form.args_schema); } catch { throw new Error('args_schema: JSON inválido'); }

      const payload: SlashCommandCreate = {
        slug: form.slug,
        name: form.name,
        description: form.description || undefined,
        handler_type: form.handler_type,
        handler_ref: form.handler_ref,
        handler_config: handlerConfig,
        args_schema: argsSchema,
        scopes: form.scopes.split(',').map((s) => s.trim()).filter(Boolean),
        sync_mode: form.sync_mode,
        is_active: form.is_active,
        is_global: form.is_global,
      };

      if (editing) {
        const updated = await api.updateSlashCommand(editing.id, payload);
        setCommands((prev) => prev.map((c) => (c.id === editing.id ? updated : c)));
      } else {
        const created = await api.createSlashCommand(payload);
        setCommands((prev) => [created, ...prev]);
      }
      closeForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro salvando');
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await api.deleteSlashCommand(deleteTarget.id);
      setCommands((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro deletando');
    } finally {
      setSubmitting(false);
    }
  }

  if (!isAdmin) {
    return (
      <div className="p-6">
        <EmptyState icon={Shield} title="Acesso negado" description="Apenas admins podem gerenciar slash commands." />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Slash Commands"
        description="Gerencia comandos / disponíveis nos canais (Telegram, Dashboard)."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
              <RefreshCw className="w-4 h-4 mr-1" /> Recarregar
            </Button>
            <Button variant="brand" size="sm" onClick={openCreate}>
              <Plus className="w-4 h-4 mr-1" /> Novo
            </Button>
          </div>
        }
      />

      {error && <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-400">{error}</div>}

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
          </div>
        ) : commands.length === 0 ? (
          <EmptyState icon={Plus} title="Nenhum command" description="Crie o primeiro slash command." />
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-raios-border bg-raios-surface-hover">
              <tr>
                <th className="px-3 py-2 text-left">slug</th>
                <th className="px-3 py-2 text-left">name</th>
                <th className="px-3 py-2 text-left">handler</th>
                <th className="px-3 py-2 text-left">scopes</th>
                <th className="px-3 py-2 text-left">flags</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {commands.map((c) => (
                <tr key={c.id} className="border-b border-raios-border/40 hover:bg-raios-surface-hover">
                  <td className="px-3 py-2"><code className="font-mono text-raios-brand">/{c.slug}</code></td>
                  <td className="px-3 py-2">{c.name}</td>
                  <td className="px-3 py-2">
                    <Badge variant={HANDLER_TYPE_VARIANT[c.handler_type]}>{c.handler_type}</Badge>
                    <code className="ml-2 font-mono text-xs text-raios-muted">{c.handler_ref}</code>
                  </td>
                  <td className="px-3 py-2 text-xs">{c.scopes.join(', ')}</td>
                  <td className="px-3 py-2 text-xs">
                    {c.is_global && <Badge variant="brand" className="mr-1">global</Badge>}
                    {!c.is_active && <Badge variant="default" className="mr-1">inativo</Badge>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} title="Editar">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(c)} title="Deletar">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog open={creating || editing !== null} onOpenChange={(open) => !open && closeForm()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? `Editar /${editing.slug}` : 'Novo slash command'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-raios-muted">slug *</label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="session-status" disabled={!!editing} />
              </div>
              <div>
                <label className="text-xs text-raios-muted">name *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Status da sessão" />
              </div>
            </div>
            <div>
              <label className="text-xs text-raios-muted">description</label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-raios-muted">handler_type *</label>
                <Select
                  value={form.handler_type}
                  onChange={(value) => setForm({ ...form, handler_type: value as SlashHandlerType })}
                  options={HANDLER_TYPES.map((t) => ({ value: t, label: t }))}
                />
              </div>
              <div>
                <label className="text-xs text-raios-muted">handler_ref *</label>
                <Input value={form.handler_ref} onChange={(e) => setForm({ ...form, handler_ref: e.target.value })} placeholder="open_session ou $args.slug" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-raios-muted">scopes (csv)</label>
                <Input value={form.scopes} onChange={(e) => setForm({ ...form, scopes: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-raios-muted">sync_mode</label>
                <Select
                  value={form.sync_mode}
                  onChange={(value) => setForm({ ...form, sync_mode: value as SlashSyncMode })}
                  options={SYNC_MODES.map((m) => ({ value: m, label: m }))}
                />
              </div>
            </div>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                ativo
              </label>
              <label className="flex items-center gap-1">
                <input type="checkbox" checked={form.is_global} onChange={(e) => setForm({ ...form, is_global: e.target.checked })} />
                global (admin RAIOS)
              </label>
            </div>
            <div>
              <label className="text-xs text-raios-muted">handler_config (JSON)</label>
              <Textarea value={form.handler_config} onChange={(e) => setForm({ ...form, handler_config: e.target.value })} rows={3} className="font-mono text-xs" />
            </div>
            <div>
              <label className="text-xs text-raios-muted">args_schema (JSON)</label>
              <Textarea value={form.args_schema} onChange={(e) => setForm({ ...form, args_schema: e.target.value })} rows={3} className="font-mono text-xs" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeForm} disabled={submitting}>Cancelar</Button>
            <Button variant="brand" onClick={() => void submit()} disabled={submitting || !form.slug || !form.name || !form.handler_ref}>
              {submitting ? 'Salvando...' : editing ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deletar /{deleteTarget?.slug}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-raios-muted py-2">Soft-delete (marca deleted_at). Pode ser revertido manualmente via DB.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={submitting}>Cancelar</Button>
            <Button variant="destructive" onClick={() => void confirmDelete()} disabled={submitting}>
              {submitting ? 'Deletando...' : 'Deletar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
